/**
 * Notifications utility for PWA
 * Uses the Web Notifications API for local notifications
 * Note: Full push notifications require a backend service
 */

export type NotificationPermission = 'default' | 'granted' | 'denied'

/**
 * Check if notifications are supported
 */
export function isNotificationSupported(): boolean {
  return 'Notification' in window
}

/**
 * Get current notification permission status
 */
export function getNotificationPermission(): NotificationPermission {
  if (!isNotificationSupported()) return 'denied'
  return Notification.permission
}

/**
 * Request notification permission from user
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!isNotificationSupported()) {
    console.warn('[Notifications] Not supported in this browser')
    return 'denied'
  }

  try {
    const permission = await Notification.requestPermission()
    console.log('[Notifications] Permission:', permission)
    return permission
  } catch (error) {
    console.error('[Notifications] Error requesting permission:', error)
    return 'denied'
  }
}

/**
 * Show a notification
 */
export function showNotification(
  title: string,
  options?: {
    body?: string
    icon?: string
    badge?: string
    tag?: string
    data?: Record<string, unknown>
    requireInteraction?: boolean
    silent?: boolean
    onClick?: () => void
  }
): Notification | null {
  if (!isNotificationSupported()) return null
  if (Notification.permission !== 'granted') return null

  try {
    const notification = new Notification(title, {
      body: options?.body,
      icon: options?.icon || '/icons/icon-192.png',
      badge: options?.badge || '/icons/icon-192.png',
      tag: options?.tag,
      data: options?.data,
      requireInteraction: options?.requireInteraction,
      silent: options?.silent,
    })

    if (options?.onClick) {
      notification.onclick = () => {
        window.focus()
        options.onClick?.()
        notification.close()
      }
    }

    return notification
  } catch (error) {
    console.error('[Notifications] Error showing notification:', error)
    return null
  }
}

// Scheduled notifications storage key
const SCHEDULED_KEY = 'waifudo-scheduled-notifications'

interface ScheduledNotification {
  id: string
  title: string
  body?: string
  scheduledTime: number // timestamp
  tag?: string
  data?: Record<string, unknown>
}

/**
 * Get scheduled notifications from storage
 */
function getScheduledNotifications(): ScheduledNotification[] {
  try {
    const stored = localStorage.getItem(SCHEDULED_KEY)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

/**
 * Save scheduled notifications to storage
 */
function saveScheduledNotifications(notifications: ScheduledNotification[]): void {
  localStorage.setItem(SCHEDULED_KEY, JSON.stringify(notifications))
}

/**
 * Schedule a notification for a specific time
 * Note: This only works while the app is open or service worker is active
 */
export function scheduleNotification(
  id: string,
  title: string,
  scheduledTime: Date,
  options?: {
    body?: string
    tag?: string
    data?: Record<string, unknown>
  }
): void {
  const scheduled = getScheduledNotifications()

  // Remove existing notification with same id
  const filtered = scheduled.filter((n) => n.id !== id)

  // Add new scheduled notification
  filtered.push({
    id,
    title,
    body: options?.body,
    scheduledTime: scheduledTime.getTime(),
    tag: options?.tag,
    data: options?.data,
  })

  saveScheduledNotifications(filtered)
  console.log('[Notifications] Scheduled:', { id, title, scheduledTime })
}

/**
 * Cancel a scheduled notification
 */
export function cancelScheduledNotification(id: string): void {
  const scheduled = getScheduledNotifications()
  const filtered = scheduled.filter((n) => n.id !== id)
  saveScheduledNotifications(filtered)
  console.log('[Notifications] Cancelled:', id)
}

/**
 * Check and fire any due scheduled notifications
 * Call this periodically (e.g., every minute)
 */
export function checkScheduledNotifications(): void {
  if (Notification.permission !== 'granted') return

  const now = Date.now()
  const scheduled = getScheduledNotifications()
  const due = scheduled.filter((n) => n.scheduledTime <= now)
  const remaining = scheduled.filter((n) => n.scheduledTime > now)

  // Fire due notifications
  for (const notification of due) {
    showNotification(notification.title, {
      body: notification.body,
      tag: notification.tag,
      data: notification.data,
    })
  }

  // Update storage with remaining notifications
  if (due.length > 0) {
    saveScheduledNotifications(remaining)
    console.log('[Notifications] Fired', due.length, 'notifications')
  }
}

// Pre-built notification templates
export const NotificationTemplates = {
  taskReminder: (taskTitle: string, dueTime?: string) => ({
    title: 'Quest Reminder!',
    body: dueTime
      ? `"${taskTitle}" is due at ${dueTime}`
      : `Don't forget: "${taskTitle}"`,
  }),

  streakWarning: (streakDays: number) => ({
    title: "Don't break your streak!",
    body: `You have a ${streakDays}-day streak. Complete a habit today!`,
  }),

  dailyReward: () => ({
    title: 'Daily Reward Available!',
    body: 'Open WaifuDo to claim your daily reward!',
  }),

  levelUp: (newLevel: number) => ({
    title: 'Level Up!',
    body: `Congratulations! You reached level ${newLevel}!`,
  }),

  characterMessage: (characterName: string, message: string) => ({
    title: `${characterName} says:`,
    body: message,
  }),

  emoteReceived: (partnerName: string, emoteName: string, emoji: string) => ({
    title: `${emoji} ${partnerName} sent you an emote!`,
    body: emoteName,
    tag: 'emote-received',
  }),

  questAssigned: (partnerName: string, questTitle: string) => ({
    title: `🗡️ New Quest from ${partnerName}!`,
    body: questTitle,
    tag: 'quest-assigned',
  }),

  partnerJoined: (partnerName: string) => ({
    title: `💕 New Partner!`,
    body: `${partnerName} has accepted your invite!`,
    tag: 'partner-joined',
  }),
}
